/**
 * Volet de synthèse — helpers NON-composants de `primitives.tsx`.
 *
 * Extraits du module de primitives pour que celui-ci n'exporte plus que des
 * composants (Fast Refresh / `react-refresh/only-export-components`). Le
 * contenu est inchangé : mêmes fonctions, mêmes signatures.
 */

import { formatCurrency } from '@/lib/currency'
import { instantDansLeFuseau } from '@/lib/timezoneDisplay'
import type { SummaryFormat, SummaryTone } from './types'

// ── Tons → classes (couleur toujours couplée à une icône, DS §1.3bis) ────────

/**
 * Les tons du DESIGN SYSTEM : ceux du volet de synthèse, plus `info`.
 *
 * ⚠️ `info` n'entre PAS dans `SummaryTone`, et c'est voulu : ce type est le
 * miroir strict du schéma Pydantic du volet, et le serveur n'émet jamais
 * `info`. L'y ajouter ferait accepter à `tsc` une valeur que rien ne produit.
 * `info` est un ton d'ÉCRAN — une note qui explique, jamais un état qui alerte
 * (DS §19.2, arbitrage PO du 2026-09-11 : il remplace le bleu brut
 * `border-blue-300 bg-blue-50` que chaque note réécrivait à la main).
 */
export type DsTone = SummaryTone | 'info'

/** Chip de badge/statut (zone [2]) + puce inline dans un `dl`. */
export function toneChipClass(tone: DsTone | null | undefined): string {
    switch (tone) {
        case 'info':
            return 'bg-info-bg text-info border-info/30'
        case 'success':
            return 'bg-success-bg text-success border-success/30'
        case 'warning':
            return 'bg-warning-bg text-warning border-warning/30'
        case 'destructive':
            return 'bg-destructive-bg text-destructive border-destructive/30'
        case 'primary':
            return 'bg-primary/10 text-primary border-primary/20'
        default:
            return 'bg-muted text-muted-foreground border-transparent'
    }
}

/** Couleur d'une valeur de `dl` (ex. TVA invalide, marge négative). */
export function toneValueClass(tone: SummaryTone | null | undefined): string {
    switch (tone) {
        case 'success':
            return 'text-success font-semibold'
        case 'warning':
            return 'text-warning font-semibold'
        case 'destructive':
            return 'text-destructive font-semibold'
        case 'strong':
            return 'text-foreground font-semibold'
        default:
            return 'text-foreground'
    }
}

/** Couleur du chiffre d'une tuile KPI. */
export function toneKpiClass(tone: SummaryTone | null | undefined): string {
    switch (tone) {
        case 'success':
            return 'text-success'
        case 'warning':
            return 'text-warning'
        case 'destructive':
            return 'text-destructive'
        default:
            return 'text-foreground'
    }
}

/**
 * Bandeau d'alerte pleine largeur (zone [3]).
 *
 * ⚠️ `info` porte en plus `[&>svg]:text-info` : l'`Alert` du socle peint son
 * icône en `text-foreground`, et une note bleue à l'icône noire se lit comme
 * deux objets.
 */
export function toneAlertClass(tone: 'warning' | 'destructive' | 'info'): string {
    if (tone === 'info') return 'bg-info-bg text-info border-info/35 [&>svg]:text-info'
    return tone === 'destructive'
        ? 'bg-destructive-bg text-destructive border-destructive/35'
        : 'bg-warning-bg text-warning border-warning/35'
}

// ── Formatage des valeurs (Money / pct / date / int / text) ──────────────────

/**
 * Formate une valeur selon `format`. `money` tente un parse numérique puis
 * `formatCurrency` (devise + locale) ; en cas d'échec (ex. fourchette
 * « 89 – 140 € » déjà mise en forme côté back), la valeur brute est renvoyée.
 */
export function formatSummaryValue(
    value: string,
    format: SummaryFormat | null | undefined,
    currency: string | null | undefined,
    lang: string,
    /**
     * Fuseau du LIEU auquel appartient l'instant (`format: 'datetime'` seul).
     * Absent partout ailleurs : l'heure d'une action appartient au lecteur.
     */
    tz?: string | null,
): string {
    if (!format || format === 'text' || format === 'int') return value
    const locale = lang.startsWith('en') ? 'en-GB' : 'fr-FR'
    if (format === 'money') {
        const n = Number(value)
        if (Number.isFinite(n)) return formatCurrency(n, currency || 'EUR', locale)
        return value
    }
    // `score10` : une note de satisfaction. Le « / 10 » n'est pas décoratif —
    // « 8,5 » seul se lit aussi bien sur 5, sur 10 ou sur 20, et l'échelle change
    // complètement le jugement qu'on porte (retour PO 2026-08-11).
    if (format === 'score10') {
        const n = Number(value)
        if (Number.isFinite(n)) {
            return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n)} / 10`
        }
        return value
    }
    // `days` : un nombre de jours. « Temps de résolution » (D146) ne porte plus
    // l'unité que disait « Jours de résolution » : c'est la valeur qui la dit,
    // sinon « 5 » se lit en heures aussi bien qu'en jours.
    if (format === 'days') {
        const n = Number(value)
        // `Number('')` vaut 0 : une valeur absente ne devient pas « 0 j ».
        if (value.trim() !== '' && Number.isFinite(n)) {
            return new Intl.NumberFormat(locale, { style: 'unit', unit: 'day', unitDisplay: 'short' }).format(n)
        }
        return value
    }
    if (format === 'pct') {
        const n = Number(value)
        if (Number.isFinite(n)) {
            return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n) + ' %'
        }
        return value
    }
    if (format === 'date') {
        const d = new Date(value)
        if (!Number.isNaN(d.getTime())) {
            return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(d)
        }
        return value
    }
    // `datetime` : le jour ET l'heure. Réservé aux valeurs dont l'heure porte la
    // réponse — « occupée jusqu'à 15:30 ». En `date`, deux instants du même jour
    // rendent la MÊME phrase, et une fin d'occupation au 17 se lit « le 17 » à
    // côté d'une libération « le 16 » : l'écran se contredit tout seul.
    // `medium` (et non `long`) parce que la ligne porte désormais deux
    // informations : « 16 sept. 2026, 15:30 » tient, « 16 septembre 2026 à
    // 15:30 » déborde la colonne du volet.
    if (format === 'datetime') {
        const d = new Date(value)
        if (!Number.isNaN(d.getTime())) return instantDansLeFuseau(d, locale, tz)
        return value
    }
    return value
}

/* ⛔ **Plus de convertisseur ici.** « Faut-il mentionner le fuseau ? » se
 * pose désormais sur TROIS surfaces — ce volet, la bulle de la carte, et les
 * constats de conflit d'une réservation de matériel (2026-09-19). Trois
 * écritures, ce sont trois réponses le jour où la règle bouge : elle vit
 * maintenant dans `@/lib/timezoneDisplay.instantDansLeFuseau`, avec le défaut
 * de l'écran lu en janvier qu'elle répare. */
